import { useCallback, useEffect, useState } from "react";
import { Edit3, Plus, Power, PowerOff, Printer, TestTube2, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as printerApi from "@/lib/printerApi";

const blank = {
  name: "",
  ip_address: "",
  port: 9100,
  location: "",
  kot_copies: 1,
  bill_copies: 1,
  column_width: 48,
  kot_font: "normal",
  bill_font: "normal",
  cut_mode: "partial",
  kick_drawer: false,
  fallback_printer_id: null,
  header: "",
  footer: "",
  font_style: "normal",
  // Tri-state: null = "no per-printer opinion" — the KOT then follows the
  // outlet-wide kot_show_captain setting (§18 inheritance on the device surface).
  show_captain_name: null,
  // Per-section bold (e5a9d1f47b32): null/empty = today's intrinsic bolds.
  // Non-empty = ONLY these sections print bold (supersedes whole-slip weight).
  bold_sections: [],
  drawer_pin: 2,
  feed_lines: 3,
  uppercase_items: false,
  font_family: "A",
};

// Station-wise content overrides (d4b7c9e02f51): same tri-state law — null =
// inherit the outlet-wide setting of the same name minus the "_p" suffix.
// Grouped BY DOCUMENT, not by printer: there is no such thing as a "KOT
// printer" or a "bill printer" here — routing decides per document which
// stations print it (bills: transaction → table → terminal → outlet default;
// KOTs: menu item → station → fallback), so one physical printer is usually
// both at once. Each group below configures one side of that dual role.
const CONTENT_GROUPS = [
  {
    heading: "On the KOTs this station receives",
    toggles: [
      ["kot_show_table_p", "Table number"],
      ["kot_show_item_notes_p", "Item notes"],
      ["kot_show_modifiers_p", "Modifiers"],
      ["kot_show_number_p", "KOT number line"],
      ["kot_show_date_p", "Date/time"],
    ],
  },
  {
    heading: "On the bills this station receives",
    toggles: [
      ["bill_show_customer_p", "Customer block (Name / Company / GSTIN)"],
    ],
  },
];
const CONTENT_TOGGLES = CONTENT_GROUPS.flatMap(g => g.toggles);

// Per-section bold vocabulary (e5a9d1f47b32) — mirrors the backend validator.
const BOLD_SECTION_NAMES = new Set(["header", "items", "totals", "footer"]);
const BOLD_SECTION_LABELS = [
  ["header", "Header block"],
  ["items", "Item rows"],
  ["totals", "Grand Total"],
  ["footer", "Footer lines"],
];
for (const [key] of CONTENT_TOGGLES) blank[key] = null;

// Print sizes (owner, 2026-11-02: "only gave 2 options for each"); the server's
// escpos.FONT_SIZES. A wider size prints fewer characters a line.
const FONTS = [
  ["normal", "Normal"],
  ["tall", "Tall (double height)"],
  ["tall3", "Taller (triple height)"],
  ["wide", "Wide (double width)"],
  ["large", "Large (double size)"],
  ["xlarge", "Extra large (triple size)"],
];
const font = value => (FONTS.some(([name]) => name === value) ? value : "normal");

const message = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the local server";

const call = async fn => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return { ok: false, error: message(error) };
  }
};

const positive = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : fallback;
};
// Zero is a real answer for feed lines: the cut already carries the footer past
// the blade, and 0 is how the bill's empty tail is removed (owner, 2026-10-31:
// "a lot of margin below the footer"). positive() turned a typed 0 back into 3.
const nonNegative = (value, fallback) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
};

const statusOf = printer => {
  if (!printer.is_active) return "disabled";
  return printer.is_online ? "online" : "offline";
};

export default function PrinterRegistry() {
  const { token } = usePOS();
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [working, setWorking] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const result = await call(() => printerApi.listPrinters(token));
    if (result.ok) setPrinters(result.data);
    setError(result.ok ? null : result.error);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Two rows on one ip:port double every job routed to that device, and the
  // print worker cannot tell them apart -- a job carries the address, not the
  // row id. Nothing stopped it: the API validates only that the string parses
  // as an IP, and `printers.ip_address` is a plain INET with no constraint.
  // Warned rather than blocked, because one physical device genuinely can serve
  // two roles (this outlet ran Garden (Bar) and Garden (Bill) on 192.168.1.16
  // for exactly that reason). First save explains the clash; saving again goes
  // through.
  const [endpointClash, setEndpointClash] = useState(null);
  const clashingPrinter = () => {
    const ip = draft?.ip_address?.trim();
    const port = positive(draft?.port, 9100);
    if (!ip) return null;
    return printers.find(p => p.id !== draft.id && String(p.ip_address) === ip && Number(p.port) === port) || null;
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Printer name is required");
    if (!draft.ip_address.trim()) return toast.error("A network printer needs an IP address");

    const clash = clashingPrinter();
    const endpoint = `${draft.ip_address.trim()}:${positive(draft.port, 9100)}`;
    if (clash && endpointClash !== endpoint) {
      setEndpointClash(endpoint);
      return toast.error(
        `${endpoint} is already ${clash.name}. Two printers on one device print every routed job twice. Save again to do it anyway.`,
        { duration: 8000 }
      );
    }
    setEndpointClash(null);

    const body = {
      name: draft.name.trim(),
      ip_address: draft.ip_address.trim(),
      port: positive(draft.port, 9100),
      location: draft.location?.trim() || "",
      kot_copies: positive(draft.kot_copies, 1),
      bill_copies: positive(draft.bill_copies, 1),
      column_width: positive(draft.column_width, 48),
      kot_font: font(draft.kot_font),
      bill_font: font(draft.bill_font),
      font_style: draft.font_style === "bold" ? "bold" : "normal",
      cut_mode: draft.cut_mode === "full" ? "full" : "partial",
      kick_drawer: !!draft.kick_drawer,
      fallback_printer_id: draft.fallback_printer_id ? Number(draft.fallback_printer_id) : null,
      // Empty string → null: the printer carries no opinion and inherits.
      header: draft.header?.trim() || null,
      footer: draft.footer?.trim() || null,
      show_captain_name: typeof draft.show_captain_name === "boolean" ? draft.show_captain_name : null,
      bold_sections: (draft.bold_sections || []).length ? draft.bold_sections.join(",") : null,
      drawer_pin: Number(draft.drawer_pin) === 5 ? 5 : 2,
      feed_lines: nonNegative(draft.feed_lines, 3),
      uppercase_items: !!draft.uppercase_items,
      font_family: draft.font_family === "B" ? "B" : "A",
      // Station-wise content overrides (d4b7c9e02f51): "inherit" → null.
      ...Object.fromEntries(CONTENT_TOGGLES.map(([key]) => [
        key,
        typeof draft[key] === "boolean" ? draft[key] : null,
      ])),
    };

    setSaving(true);
    const result = draft.id
      ? await call(() => printerApi.updatePrinter(draft.id, body, token))
      : await call(() => printerApi.createPrinter(body, token));
    setSaving(false);
    if (!result.ok) return toast.error(result.error);

    toast.success(draft.id ? `${body.name} updated` : `${body.name} added`);
    setDraft(null);
    await load();
  };

  const toggleActive = async printer => {
    setBusyId(printer.id);
    const result = printer.is_active
      ? await call(() => printerApi.disablePrinter(printer.id, token))
      : await call(() => printerApi.enablePrinter(printer.id, token));
    setBusyId(null);
    if (!result.ok) return toast.error(result.error);
    toast.success(printer.is_active ? `${printer.name} disabled` : `${printer.name} enabled`);
    await load();
  };

  const test = async printer => {
    setBusyId(printer.id);
    const result = await call(() => printerApi.testPrint(printer.id, token));
    setBusyId(null);
    if (!result.ok) return toast.error(`Test print failed — ${result.error}`);
    toast.success(`Test print queued for ${printer.name} · job #${result.data.job_id}`);
  };

  const confirmDelete = async () => {
    setWorking(true);
    const result = await call(() => printerApi.deletePrinter(confirming.id, token));
    setWorking(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`${confirming.name} deleted`);
    setConfirming(null);
    await load();
  };

  return <section className="bg-card border rounded-2xl shadow-sm flex flex-col h-full" data-testid="printer-registry-panel">
    <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 border-b">
      <div className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Printer className="w-5 h-5"/>
        </span>
        <div>
          <h3 className="text-title font-semibold text-foreground">ESC/POS printer registry</h3>
          <p className="text-meta text-muted-foreground font-medium">The printers the local print worker drains. Changes apply to every terminal.</p>
        </div>
      </div>
        <Button variant="action" className="font-bold shadow-sm" onClick={() => setDraft({ ...blank })} data-testid="add-printer-button"><Plus className="w-4 h-4 mr-2"/>Add printer</Button>
    </header>

    {error && (
      <div className="bg-destructive/10 text-destructive border border-destructive/20 p-4 rounded-xl flex items-start gap-3" role="alert" data-testid="printer-registry-error">
        <TriangleAlert className="w-5 h-5 shrink-0 mt-0.5"/>
        <span className="text-body font-semibold">{error}</span>
      </div>
    )}

    <div className="p-6">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4" data-testid="printer-list">
        {printers.map((printer, index) => {
          const status = statusOf(printer);
          const statusColors = {
            online: "bg-success-subtle text-success",
            offline: "bg-danger-subtle text-danger",
            disabled: "bg-muted text-muted-foreground"
          }[status];
          
          return (
            <div className={`p-4 rounded-xl border flex flex-col transition-all ${printer.is_active ? "bg-background shadow-sm hover:shadow-md" : "bg-muted/30 opacity-70"}`} key={printer.id} data-testid={`printer-${index}-row`}>
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex flex-col min-w-0 flex-1">
                  <strong className="text-lede font-semibold text-foreground leading-tight truncate">{printer.name}</strong>
                  <small className="text-meta font-medium text-muted-foreground font-mono mt-0.5 truncate">{printer.ip_address}:{printer.port}</small>
                </div>
                <span className={`shrink-0 text-meta font-medium px-2 py-0.5 rounded-md ${statusColors}`} data-testid={`printer-${index}-status`}>
                  {status}
                </span>
              </div>
              
              <div className="flex flex-col gap-1 mb-4">
                <b className="text-body font-semibold text-foreground truncate">{printer.location || "No location set"}</b>
                <small className="text-meta text-muted-foreground font-medium truncate">{printer.kot_copies} KOT · {printer.bill_copies} bill · {printer.column_width} col</small>
              </div>
              
              <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
                <Button variant="ghost" size="sm" className={`h-8 px-2 font-bold ${printer.is_active ? 'text-warning hover:text-warning hover:bg-warning-subtle' : 'text-success hover:text-success hover:bg-success-subtle'}`} onClick={() => toggleActive(printer)} disabled={busyId === printer.id} data-testid={`printer-${index}-toggle-button`} aria-label={`${printer.is_active ? "Disable" : "Enable"} ${printer.name}`}>
                  {printer.is_active ? <><PowerOff className="w-3.5 h-3.5 mr-1.5"/>Disable</> : <><Power className="w-3.5 h-3.5 mr-1.5"/>Enable</>}
                </Button>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => test(printer)} disabled={busyId === printer.id} data-testid={`printer-${index}-test-button`} aria-label={`Test print on ${printer.name}`}><TestTube2 className="w-4 h-4 text-muted-foreground"/></Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setDraft({
                    ...printer,
                    location: printer.location || "",
                    bold_sections: printer.bold_sections ? printer.bold_sections.split(",").filter(part => BOLD_SECTION_NAMES.has(part)) : [],
                    drawer_pin: Number(printer.drawer_pin) === 5 ? 5 : 2,
                    feed_lines: printer.feed_lines ?? 3,
                    uppercase_items: !!printer.uppercase_items,
                    font_family: printer.font_family === "B" ? "B" : "A",
                  })} data-testid={`printer-${index}-edit-button`} aria-label={`Edit ${printer.name}`}><Edit3 className="w-4 h-4 text-muted-foreground"/></Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-danger hover:text-danger hover:bg-danger-subtle" onClick={() => setConfirming(printer)} data-testid={`printer-${index}-delete-button`} aria-label={`Delete ${printer.name}`}><Trash2 className="w-4 h-4"/></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!printers.length && !loading && !error && (
        <div className="py-12 flex items-center justify-center border-2 border-dashed rounded-xl">
          <p className="text-meta text-muted-foreground font-medium" data-testid="printer-list-empty">
            No printers configured. Nothing will print until one is added.
          </p>
        </div>
      )}
    </div>

    {draft && <Modal
      title={draft.id ? `Edit ${draft.name}` : "Add printer"}
      description="Where it is on the network, and how its KOTs and bills look."
      onClose={() => setDraft(null)}
      testId="printer-editor-modal"
      footer={<>
        <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
        <Button variant="action" onClick={save} disabled={saving} data-testid="save-printer-button">
          {/* D2 (DESIGN.md): the button names the thing being edited, same as the modal title. */}
          {saving ? "Saving…" : `Save ${draft.name || "printer"}`}
        </Button>
      </>}
    ><div className="flex flex-col gap-4">
      {/* Name and Location share a row: the form is long, and in the old 480px box it
          was a scroll inside a small square (owner, 2026-10-19). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Printer name</span><Input value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} data-testid="printer-name-input"/></label>
      <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Location</span><Input value={draft.location} onChange={event => setDraft({ ...draft, location: event.target.value })} placeholder="Main kitchen" data-testid="printer-location-input"/></label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">IP address</span><Input value={draft.ip_address} onChange={event => setDraft({ ...draft, ip_address: event.target.value })} placeholder="192.168.1.50" data-testid="printer-ip-input"/>{clashingPrinter() && (<small className="text-meta text-warning font-medium" data-testid="printer-endpoint-clash">Also used by {clashingPrinter().name} — both would print every job routed to it.</small>)}</label>
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Port</span><Input type="number" value={draft.port} onChange={event => setDraft({ ...draft, port: event.target.value })} data-testid="printer-port-input"/></label>
      </div>
      {/* The owner's backup (2026-10-24): when this printer is off the network its
          tickets print there, headed with this printer's name, instead of waiting. */}
      <label className="flex flex-col gap-1.5">
        <span className="text-body font-semibold">If this printer is down, print on</span>
        <select
          className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50"
          value={draft.fallback_printer_id ?? ""}
          onChange={event => setDraft({ ...draft, fallback_printer_id: event.target.value ? Number(event.target.value) : null })}
          data-testid="printer-fallback-select"
        >
          <option value="">No backup: hold tickets until it is back</option>
          {printers.filter(p => p.id !== draft.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-3 gap-4">
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">KOT copies</span><Input type="number" min="1" value={draft.kot_copies} onChange={event => setDraft({ ...draft, kot_copies: event.target.value })} data-testid="printer-kot-copies-input"/></label>
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Bill copies</span><Input type="number" min="1" value={draft.bill_copies} onChange={event => setDraft({ ...draft, bill_copies: event.target.value })} data-testid="printer-bill-copies-input"/></label>
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Columns</span><Input type="number" min="1" value={draft.column_width} onChange={event => setDraft({ ...draft, column_width: event.target.value })} data-testid="printer-column-width-input"/></label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Font family</span>
          <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={draft.font_family === "B" ? "B" : "A"} onChange={event => setDraft({ ...draft, font_family: event.target.value })} data-testid="printer-font-family-select">
            <option value="A">A — standard (12×24)</option>
            <option value="B">B — condensed (9×17)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">KOT text size</span>
          <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={font(draft.kot_font)} onChange={event => setDraft({ ...draft, kot_font: event.target.value })} data-testid="printer-kot-font-select">
            {FONTS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Bill text size</span>
          <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={font(draft.bill_font)} onChange={event => setDraft({ ...draft, bill_font: event.target.value })} data-testid="printer-bill-font-select">
            {FONTS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5"><span className="text-body font-semibold">Cut behavior</span>
          <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={draft.cut_mode || "partial"} onChange={event => setDraft({ ...draft, cut_mode: event.target.value })} data-testid="printer-cut-mode-select">
            <option value="partial">Partial Cut (leave a tab)</option>
            <option value="full">Full Cut</option>
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-body font-semibold">Drawer kick pin <span className="font-normal text-muted-foreground">(when kick is on)</span></span>
          <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={Number(draft.drawer_pin) === 5 ? "5" : "2"} onChange={event => setDraft({ ...draft, drawer_pin: Number(event.target.value) })} data-testid="printer-drawer-pin-select">
            <option value="2">Pin 2 (standard)</option>
            <option value="5">Pin 5</option>
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-row items-center gap-2 cursor-pointer mt-1">
          <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary" checked={!!draft.kick_drawer} onChange={event => setDraft({ ...draft, kick_drawer: event.target.checked })} data-testid="printer-kick-drawer-checkbox" />
          <span className="text-body font-semibold select-none">Kick cash drawer on bills</span>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-body font-semibold">Feed lines before cut <span className="font-normal text-muted-foreground">(3 default)</span></span>
          <Input type="number" min="0" max="10" value={draft.feed_lines} onChange={event => setDraft({ ...draft, feed_lines: event.target.value })} data-testid="printer-feed-lines-input"/>
        </label>
      </div>
      <label className="flex flex-row items-center gap-2 cursor-pointer">
        <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary" checked={!!draft.uppercase_items} onChange={event => setDraft({ ...draft, uppercase_items: event.target.checked })} data-testid="printer-uppercase-items-checkbox" />
        <span className="text-body font-semibold select-none">Item names in CAPS on KOTs</span>
      </label>

      {/* Stationery overrides (§18): a printer with its own header/footer is
          printing its own stationery — the outlet identity block and standing
          footer are replaced, not added to. Empty means "use the outlet's". */}
      <div className="grid grid-cols-1 gap-4 border-t pt-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-body font-semibold">Header override <span className="font-normal text-muted-foreground">(optional — replaces outlet name/GSTIN block)</span></span>
          <Input value={draft.header || ""} onChange={event => setDraft({ ...draft, header: event.target.value })} placeholder="Bar Station" data-testid="printer-header-input"/>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-body font-semibold">Footer line override <span className="font-normal text-muted-foreground">(optional — replaces bill footers)</span></span>
          <Input value={draft.footer || ""} onChange={event => setDraft({ ...draft, footer: event.target.value })} placeholder="" data-testid="printer-footer-input"/>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-body font-semibold">Text weight</span>
            <select className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={draft.font_style === "bold" ? "bold" : "normal"} onChange={event => setDraft({ ...draft, font_style: event.target.value })} data-testid="printer-font-style-select">
              <option value="normal">Normal</option>
              <option value="bold">Bold (whole slip)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-body font-semibold">Captain name on this KOT</span>
            <select
              className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={typeof draft.show_captain_name === "boolean" ? String(draft.show_captain_name) : "inherit"}
              onChange={event => setDraft({ ...draft, show_captain_name: event.target.value === "inherit" ? null : event.target.value === "true" })}
              data-testid="printer-show-captain-select"
            >
              <option value="inherit">Inherit from outlet setting</option>
              <option value="true">Always show</option>
              <option value="false">Always hide</option>
            </select>
          </label>
        </div>
      </div>
      {/* Station-wise content overrides (d4b7c9e02f51): each select is
          tri-state — "inherit" defers to the outlet-wide setting of the same
          name; show/hide is this printer's own decision and wins outright.
          Grouped by document because routing, not printer type, decides which
          stations receive KOTs and which receive bills. */}
      {CONTENT_GROUPS.map(group => (
        <div key={group.heading} className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
          <span className="text-body font-bold col-span-full">{group.heading}</span>
          {group.toggles.map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1.5">
              <span className="text-body font-semibold">{label}</span>
              <select
                className="h-10 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={typeof draft[key] === "boolean" ? String(draft[key]) : "inherit"}
                onChange={event => setDraft({ ...draft, [key]: event.target.value === "inherit" ? null : event.target.value === "true" })}
                data-testid={`printer-${key}-select`}
              >
                <option value="inherit">Inherit from outlet setting</option>
                <option value="true">Show</option>
                <option value="false">Hide</option>
              </select>
            </label>
          ))}
        </div>
      ))}
      {/* Per-section bold (e5a9d1f47b32): checking any box switches this
          printer to an explicit layout — ONLY the checked sections print bold,
          on both KOTs and bills. Leave all unchecked to keep today's behaviour
          (intrinsic bolds + whole-slip Text weight). */}
      <div className="border-t pt-4">
        <span className="text-body font-bold">Bold sections <span className="font-normal text-muted-foreground">(none checked = default bolding)</span></span>
        {/* D4 (DESIGN.md): even grid, labels on one line — no uneven two-line wraps. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-snug items-start mt-2">
          {BOLD_SECTION_LABELS.map(([value, label]) => (
            <label key={value} className="flex flex-row items-start gap-2 cursor-pointer min-w-0">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                checked={draft.bold_sections.includes(value)}
                onChange={event => setDraft({
                  ...draft,
                  bold_sections: event.target.checked
                    ? [...draft.bold_sections, value]
                    : draft.bold_sections.filter(v => v !== value),
                })}
                data-testid={`printer-bold-${value}-checkbox`}
              />
              <span className="text-body select-none leading-tight">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div></Modal>}

    {confirming && <Modal size="confirm" title={`Delete ${confirming.name}?`} onClose={() => setConfirming(null)} testId="delete-printer-modal"><div className="flex flex-col gap-base">
      <div className="bg-danger-subtle text-danger p-snug rounded-md border border-danger/30">
        <p className="text-meta">
          This removes it from the registry. A printer that has already printed cannot be
          deleted — disable it instead and its job history stays intact.
        </p>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => setConfirming(null)} data-testid="cancel-delete-printer-button">Keep it</Button>
        <Button variant="destructive" onClick={confirmDelete} disabled={working} data-testid="confirm-delete-printer-button">Delete Printer</Button>
      </div>
    </div></Modal>}
  </section>;
}
